import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Flex, Avatar, Text, Spinner, Heading, Button } from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";
import { ff, navClearance } from "theme";
import { ConversationsContext } from "contexts/ConversationsDataContext";
import { CollabDataContext, CollabDataLoadingContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { useMarketplaceOrders } from 'contexts/MarketplaceOrdersContext';
import { Message } from "types";
import {
  searchForMessagesWithSearchTerm,
  searchForMessagesWithoutSearchTerm,
  formatConversationDate,
  getUnreadCount,
  getConversationListPreviewText,
  groupInboxConversations,
  millisOf,
  resolveCreatorArchivedConversations,
  isContentSubmitted,
} from '../utils';
import { deriveOfferCards, getCreatorOfferAvailability, latestOfferActivityMs, offerPreviewText } from '../preCollab';
import { Client } from "@foodfluence/shared";
import { useCampaignSpots } from "hooks/useCampaignSpots";
import ThreadRowMenu from "./ThreadRowMenu";

export type InboxTab = 'all' | 'unread' | 'submitted';

interface ConversationsListProps {
  tab?: InboxTab;
  onConversationClick: (conversationId: string, client?: Client) => void;
  searchTerm: string;
}

const ConversationsList: React.FC<ConversationsListProps> = ({ onConversationClick, searchTerm, tab = 'all' }) => {
  const conversations = useContext(ConversationsContext);
  const collabsData = useContext(CollabDataContext);
  const collabDataLoading = useContext(CollabDataLoadingContext);
  const clientData = useContext(ClientDataContext);
  const influencerCampaignData = useContext(InfluencerCampaignDataContext);
  const { orders: marketplaceOrders, loading: marketplaceOrdersLoading } = useMarketplaceOrders();
  const [filteredConversations, setFilteredConversations] = useState<{ conversationId: string, message: Message }[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const isLoading = collabDataLoading || marketplaceOrdersLoading;
  const archivedIds = useMemo(() => resolveCreatorArchivedConversations(conversations), [conversations]);
  const sections = useMemo(
    () => groupInboxConversations(filteredConversations, collabsData, archivedIds, marketplaceOrders),
    [filteredConversations, collabsData, archivedIds, marketplaceOrders],
  );
  const rowMatchesTab = ({ conversationId }: { conversationId: string }) => {
    if (tab === 'unread') return getUnreadCount(conversations[conversationId]?.messages_log) > 0;
    if (tab === 'submitted') return isContentSubmitted(collabsData.find((c) => c.id === conversationId));
    return true;
  };
  const visible = {
    potential: sections.potential.filter(rowMatchesTab),
    others: sections.others.filter(rowMatchesTab),
    archived: sections.archived.filter(rowMatchesTab),
  };
  const isUnreadRow = ({ conversationId }: { conversationId: string }) =>
    getUnreadCount(conversations[conversationId]?.messages_log) > 0;
  // Spots only for Potential rows' campaigns with a cap — the rows that can read "Campaign full".
  const potentialCampaignIds = useMemo(
    () =>
      sections.potential.flatMap(({ conversationId }) => {
        const collab = collabsData.find((c) => c.id === conversationId);
        const campaignId = collab?.influencerCampaignID;
        return collab && campaignId && influencerCampaignData[collab.id]?.maxCollabs != null ? [campaignId] : [];
      }),
    [sections.potential, collabsData, influencerCampaignData],
  );
  // Refetch when a Potential collab changes status or a campaign's cap changes (brand added spots).
  const potentialSpotsKey = sections.potential
    .map(({ conversationId }) => {
      const collab = collabsData.find((c) => c.id === conversationId);
      return `${conversationId}:${collab?.status}:${collab ? influencerCampaignData[collab.id]?.maxCollabs : ''}`;
    })
    .join(',');
  // ponytail: one request per capped Potential campaign per refresh; a batch spots endpoint if inboxes grow.
  const { spots: potentialSpots } = useCampaignSpots(potentialCampaignIds, potentialSpotsKey);

  useEffect(() => {
    let newFilteredConversations: { conversationId: string, message: Message }[] = [];
    const addedMessages = new Set<string>();

    if (!searchTerm || /^\s*$/.test(searchTerm)) {
      newFilteredConversations = searchForMessagesWithoutSearchTerm(
        conversations,
        collabsData,
        influencerCampaignData,
        marketplaceOrders,
      );
      // Order by latest activity: a counter received today puts the thread above older chats even
      // if its last message is from last week. Search results keep their match order.
      const activityMs = ({ conversationId, message }: { conversationId: string; message: Message }) => {
        const collab = collabsData.find((c) => c.id === conversationId);
        const campaign = collab ? influencerCampaignData[collab.id] : undefined;
        return Math.max(
          millisOf(message?.time_sent) || millisOf(conversations[conversationId]?.time_created),
          latestOfferActivityMs(collab, campaign),
        );
      };
      newFilteredConversations = newFilteredConversations
        .map((item) => ({ item, at: activityMs(item) }))
        .sort((a, b) => b.at - a.at)
        .map(({ item }) => item);
    } else {
      newFilteredConversations = searchForMessagesWithSearchTerm(
        conversations,
        searchTerm,
        addedMessages,
        collabsData,
        influencerCampaignData,
        clientData,
        marketplaceOrders,
      );
    }
    setFilteredConversations(newFilteredConversations);
  }, [conversations, searchTerm, collabsData, influencerCampaignData, clientData, marketplaceOrders]);

  const renderRow = ({ conversationId, message }: { conversationId: string; message: Message }, inPotential = false) => {
    const collab = collabsData.find((c) => c.id === conversationId);
    const marketplaceOrder = marketplaceOrders.find((o) => o.id === conversationId);
    const campaign = collab ? influencerCampaignData[collab.id] : undefined;
    // Offers live on the collab, not in messages_log. When the latest offer is newer than the
    // latest message (or there are no messages), the row previews the offer instead.
    const latestOffer = collab ? deriveOfferCards(collab, campaign).at(-1) : undefined;
    // Full precision (nanoseconds) so a message and an offer in the same second order correctly.
    const messageMs = millisOf(message?.time_sent);
    // While searching, the row shows the matching message: it's why the result is listed.
    const isSearching = !!searchTerm && !/^\s*$/.test(searchTerm);
    const previewOffer =
      !isSearching && latestOffer && latestOffer.status !== 'accepted' && latestOffer.timeMs >= messageMs
        ? latestOffer
        : null;
    const client =
      campaign?.clientID != null
        ? clientData[campaign.clientID]
        : marketplaceOrder
          ? ({
              id: marketplaceOrder.brandID,
              accountName: marketplaceOrder.brandName ?? 'Brand',
              instagramProfilePicture: marketplaceOrder.brandLogoUrl,
            } as Client)
          : undefined;
    const unreadCount = getUnreadCount(conversations[conversationId]?.messages_log);

    if (!client) {
      return null;
    }

    const submitted = !previewOffer && isContentSubmitted(collab);

    return (
      <Flex
        key={`${conversationId}-${message?.time_sent?.seconds}`}
        align="flex-start"
        gap="14px"
        py="14px"
        mx="20px"
        borderBottom="1px solid"
        borderColor={ff.line}
        onClick={() => onConversationClick(conversationId, client)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onConversationClick(conversationId, client);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open chat with ${client?.accountName ?? "brand"}${campaign?.name ? `, ${campaign.name}` : ""}`}
        cursor="pointer"
      >
        <Avatar
          src={client?.instagramProfilePicture}
          name={client?.accountName}
          w="48px"
          h="48px"
          flexShrink={0}
          bg={ff.card}
          color={ff.ink}
          fontSize="15px"
          fontWeight={600}
          sx={{ '& > div': { fontSize: '15px', fontWeight: 600 } }}
        />

        <Flex flex="1" minW={0} direction="column" gap="3px">
          <Flex align="baseline" justify="space-between" gap="10px">
            <Text fontSize="16px" lineHeight="24px" fontWeight={unreadCount > 0 ? 800 : 600} color={ff.ink} noOfLines={1}>
              {client?.accountName ?? "Conversation"}
            </Text>
            <Text fontSize="12px" lineHeight="16px" color={ff.text2} flexShrink={0}>
              {formatConversationDate(message, conversations[conversationId], previewOffer?.timeMs)}
            </Text>
          </Flex>

          {campaign?.name && (
            <Text fontSize="14px" lineHeight="20px" fontWeight={600} color={ff.body} noOfLines={1}>
              {campaign.name}
            </Text>
          )}

          <Flex align="center" gap="10px">
            {submitted ? (
              <Flex
                as="span"
                align="center"
                gap="6px"
                h="26px"
                px="10px"
                borderRadius="full"
                bg={ff.limeSurface}
                color={ff.ink}
                fontSize="13px"
                fontWeight={600}
              >
                <FiCheck size={14} strokeWidth={2.6} aria-hidden />
                Content submitted
              </Flex>
            ) : (
              <Text
                fontSize="14px"
                lineHeight="20px"
                color={ff.text2}
                fontStyle={message === null && !previewOffer ? "italic" : "normal"}
                noOfLines={1}
                flex="1"
                minW={0}
              >
                {previewOffer
                  ? offerPreviewText(
                      previewOffer,
                      client?.accountName,
                      inPotential
                        ? getCreatorOfferAvailability(collab, campaign, potentialSpots[collab?.influencerCampaignID ?? ""])
                        : undefined,
                    )
                  : message
                    ? getConversationListPreviewText(message)
                    : "No messages yet"}
              </Text>
            )}

            {unreadCount > 0 && (
              <Flex
                as="span"
                ml="auto"
                minW="22px"
                h="22px"
                px="6px"
                borderRadius="full"
                bg={ff.ink}
                color={ff.lime}
                align="center"
                justify="center"
                fontSize="12px"
                fontWeight={600}
                flexShrink={0}
                aria-label={`${unreadCount} unread`}
              >
                {unreadCount}
              </Flex>
            )}
          </Flex>
        </Flex>

        <ThreadRowMenu
          conversationId={conversationId}
          collab={collab}
          campaign={campaign}
          client={client}
          marketplaceOrderId={marketplaceOrder?.id}
          isArchived={archivedIds.has(conversationId)}
        />
      </Flex>
    );
  };

  const sectionHeading = (label: string) => (
    <Heading as="h2" fontFamily="display" fontSize="13px" lineHeight="16px" fontWeight={600} color={ff.text2} textTransform="uppercase" letterSpacing="0.08em" px="20px" mt="22px" mb="4px">
      {label}
    </Heading>
  );

  const boardGroups: { title: string; rows: typeof visible.others }[] =
    tab === 'all'
      ? [
          { title: 'Unread', rows: visible.others.filter(isUnreadRow) },
          { title: 'Earlier', rows: visible.others.filter((r) => !isUnreadRow(r)) },
        ]
      : [{ title: tab === 'unread' ? 'Unread' : 'Submitted', rows: visible.others }];
  const nothingToShow = visible.potential.length + visible.others.length + visible.archived.length === 0;

  return (
    <Box width="100%" fontFamily="display" pb={navClearance}>
      {isLoading ? (
        <Flex minH="200px" align="center" justify="center" direction="column" gap={3} py="14px">
          <Spinner size="lg" color={ff.muted} thickness="3px" />
          <Text fontSize="14px" color={ff.text2}>Loading messages...</Text>
        </Flex>
      ) : filteredConversations.length === 0 || nothingToShow ? (
        <Box px="20px" py="22px">
          <Text color={ff.text2} fontSize="15px">No conversations</Text>
        </Box>
      ) : (
        <>
          {boardGroups.map(
            ({ title, rows }) =>
              rows.length > 0 && (
                <Box as="section" aria-label={title} key={title}>
                  {sectionHeading(title)}
                  {rows.map((row) => renderRow(row))}
                </Box>
              ),
          )}
          {visible.potential.length > 0 && (
            <Box as="section" aria-label="Potential">
              {sectionHeading("Potential")}
              {visible.potential.map((row) => renderRow(row, true))}
            </Box>
          )}
          {visible.archived.length > 0 && (
            <Box as="section" aria-label="Archived">
              <Button
                variant="ghost"
                size="sm"
                mx="12px"
                mt="12px"
                h="auto"
                py="6px"
                fontFamily="display"
                color={ff.text2}
                fontWeight={600}
                _hover={{ bg: ff.grey }}
                onClick={() => setShowArchived((v) => !v)}
                aria-expanded={showArchived}
                aria-controls="inbox-archived-list"
              >
                {showArchived ? "Hide archived" : `Show archived (${visible.archived.length})`}
              </Button>
              {/* Always rendered (hidden while collapsed) so aria-controls points at a real element. */}
              <Box id="inbox-archived-list" hidden={!showArchived}>
                {showArchived && (
                  <>
                    {sectionHeading("Archived")}
                    {visible.archived.map((row) => renderRow(row))}
                  </>
                )}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ConversationsList;
